class FudaSecondProductPopup extends HTMLElement {
  connectedCallback() {
    this.panel = this.querySelector('.fuda-upsell__panel');
    this.body = this.querySelector('.fuda-upsell__body');
    this.closeButtons = this.querySelectorAll('[data-fuda-upsell-close]');
    this.tabs = this.querySelectorAll('[data-fuda-tab]');
    this.previouslyFocused = null;
    this.collectionState = new Map();
    this.pageSize = 50;

    this.closeButtons.forEach((button) => button.addEventListener('click', () => this.close()));
    this.tabs.forEach((tab) => tab.addEventListener('click', () => this.activateTab(tab.dataset.fudaTab)));
    this.body?.addEventListener('scroll', this.onBodyScroll, { passive: true });
    this.addEventListener('click', this.onPopupClick);
    document.addEventListener('keydown', this.onKeydown);
    document.addEventListener('cart:update', this.onCartUpdate);
  }

  disconnectedCallback() {
    this.body?.removeEventListener('scroll', this.onBodyScroll);
    this.removeEventListener('click', this.onPopupClick);
    document.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('cart:update', this.onCartUpdate);
  }

  onCartUpdate = (event) => {
    const cart = event.detail?.resource;
    const source = event.detail?.data?.source;
    if (!cart || typeof cart.item_count !== 'number') return;

    const eventOriginatedInsidePopup = event.target && this.contains(event.target);

    if (eventOriginatedInsidePopup || source === 'fuda-second-product-popup') {
      if (cart.item_count >= 2) this.close();
      return;
    }

    if (cart.item_count === 1 && !sessionStorage.getItem('fuda-upsell-dismissed')) {
      window.setTimeout(() => this.open(), 250);
    }
  };

  onKeydown = (event) => {
    if (this.hidden) return;
    if (event.key === 'Escape') this.close();
  };

  onBodyScroll = () => {
    if (!this.body) return;
    const distanceFromBottom = this.body.scrollHeight - this.body.scrollTop - this.body.clientHeight;
    if (distanceFromBottom < 500) {
      const activePanel = this.querySelector('[data-fuda-panel]:not(.is-hidden)');
      if (activePanel) this.loadNextPage(activePanel.dataset.fudaCollectionHandle);
    }
  };

  onPopupClick = async (event) => {
    const button = event.target.closest('[data-fuda-ajax-add]');
    if (!button) return;

    event.preventDefault();
    if (button.disabled) return;

    const variantId = button.dataset.variantId;
    if (!variantId) return;

    const originalText = button.querySelector('[data-button-text]')?.textContent || 'Sepete Ekle';
    button.disabled = true;
    const textNode = button.querySelector('[data-button-text]');
    if (textNode) textNode.textContent = 'Ekleniyor...';

    try {
      const root = window.Shopify?.routes?.root || '/';
      const addResponse = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
      });

      if (!addResponse.ok) throw new Error('Ürün sepete eklenemedi.');

      const cartResponse = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
      if (!cartResponse.ok) throw new Error('Sepet güncellenemedi.');
      const cart = await cartResponse.json();

      if (textNode) textNode.textContent = 'Sepete Eklendi';
      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: { resource: cart, data: { source: 'fuda-second-product-popup' } },
      }));
    } catch (error) {
      console.error(error);
      if (textNode) textNode.textContent = 'Tekrar Dene';
      button.disabled = false;
      return;
    }

    window.setTimeout(() => {
      button.disabled = false;
      if (textNode) textNode.textContent = originalText;
    }, 1200);
  };

  activateTab(handle) {
    this.tabs.forEach((tab) => {
      const active = tab.dataset.fudaTab === handle;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
    });

    this.querySelectorAll('[data-fuda-panel]').forEach((panel) => {
      panel.classList.toggle('is-hidden', panel.dataset.fudaPanel !== handle);
    });

    if (this.body) this.body.scrollTop = 0;
    this.ensureCollectionLoaded(handle);
  }

  async ensureCollectionLoaded(handle) {
    if (!handle) return;
    if (!this.collectionState.has(handle)) {
      this.collectionState.set(handle, { page: 0, loading: false, done: false, initialized: false });
    }
    const state = this.collectionState.get(handle);
    if (!state.initialized) {
      const panel = this.querySelector(`[data-fuda-collection-handle="${CSS.escape(handle)}"]`);
      const container = panel?.querySelector('[data-fuda-products]');
      if (container) container.innerHTML = this.loadingMarkup();
      await this.loadNextPage(handle, true);
    }
  }

  async loadNextPage(handle, replace = false) {
    if (!handle) return;
    if (!this.collectionState.has(handle)) {
      this.collectionState.set(handle, { page: 0, loading: false, done: false, initialized: false });
    }

    const state = this.collectionState.get(handle);
    if (state.loading || state.done) return;

    const panel = this.querySelector(`[data-fuda-collection-handle="${CSS.escape(handle)}"]`);
    const container = panel?.querySelector('[data-fuda-products]');
    if (!container) return;

    state.loading = true;
    const nextPage = state.page + 1;

    try {
      const root = window.Shopify?.routes?.root || '/';
      const url = `${root}collections/${encodeURIComponent(handle)}/products.json?limit=${this.pageSize}&page=${nextPage}`;
      const response = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`Koleksiyon yüklenemedi: ${response.status}`);

      const payload = await response.json();
      const products = Array.isArray(payload.products) ? payload.products : [];
      const availableProducts = products.filter((product) =>
        Array.isArray(product.variants) && product.variants.some((variant) => variant.available)
      );

      if (replace || !state.initialized) container.innerHTML = '';
      container.insertAdjacentHTML('beforeend', availableProducts.map((product) => this.productMarkup(product)).join(''));

      state.page = nextPage;
      state.initialized = true;
      state.done = products.length < this.pageSize;

      if (!state.done) {
        container.insertAdjacentHTML('beforeend', this.moreLoadingMarkup());
      } else if (container.children.length === 0) {
        container.innerHTML = '<p class="fuda-upsell__empty">Bu koleksiyonda satışa açık ürün bulunmuyor.</p>';
      }
    } catch (error) {
      console.error(error);
      if (!state.initialized) {
        container.innerHTML = '<p class="fuda-upsell__empty">Ürünler yüklenemedi. Lütfen tekrar deneyin.</p>';
      }
    } finally {
      state.loading = false;
      container.querySelectorAll('[data-fuda-more-loading]').forEach((el) => el.remove());
    }
  }

  productMarkup(product) {
    const variant = product.variants.find((item) => item.available) || product.variants[0];
    const price = Number(variant?.price || 0);
    const compareAt = Number(variant?.compare_at_price || 0);
    const memberPrice = price * 0.72;
    const image = product.images?.[0]?.src || product.images?.[0] || '';
    const productUrl = `${window.Shopify?.routes?.root || '/'}products/${product.handle}`;

    return `
      <article class="fuda-upsell__product">
        <a class="fuda-upsell__image" href="${this.escapeAttribute(productUrl)}" tabindex="-1">
          ${image ? `<img src="${this.escapeAttribute(image)}" loading="lazy" alt="${this.escapeAttribute(product.title)}">` : ''}
        </a>
        <div class="fuda-upsell__product-info">
          <a class="fuda-upsell__product-title" href="${this.escapeAttribute(productUrl)}">${this.escapeHtml(product.title)}</a>
          <div class="fuda-upsell__price">
            <span>${this.formatMoney(price)}</span>
            ${compareAt > price ? `<s>${this.formatMoney(compareAt)}</s>` : ''}
          </div>
          <div class="fuda-upsell__member-price" aria-label="FUDA üyelerine özel sepette fiyat">
            <span class="fuda-upsell__member-label">FUDA Üyelerine Özel</span>
            <span class="fuda-upsell__member-caption">Sepette Fiyatı</span>
            <strong>${this.formatMoney(memberPrice)}</strong>
            <small>FUDA20 ve 2 ürün kampanyasıyla</small>
          </div>
          <div class="fuda-upsell__quick-add">
            <button type="button" class="fuda-upsell__add-button" data-fuda-ajax-add data-variant-id="${variant.id}">
              <span class="fuda-upsell__add-icon" aria-hidden="true">+</span>
              <span data-button-text>Sepete Ekle</span>
            </button>
          </div>
        </div>
      </article>`;
  }

  formatMoney(value) {
    const currency = window.Shopify?.currency?.active || 'TRY';
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2,
    }).format(value);
  }

  escapeHtml(value = '') {
    const div = document.createElement('div');
    div.textContent = String(value);
    return div.innerHTML;
  }

  escapeAttribute(value = '') {
    return this.escapeHtml(value).replace(/`/g, '&#96;');
  }

  loadingMarkup() {
    return '<p class="fuda-upsell__empty">Ürünler yükleniyor...</p>';
  }

  moreLoadingMarkup() {
    return '<div class="fuda-upsell__more-loading" data-fuda-more-loading>Diğer ürünler yükleniyor...</div>';
  }

  open() {
    if (!this.hidden) return;
    const cartDrawer = document.querySelector('cart-drawer-component');
    if (cartDrawer && typeof cartDrawer.close === 'function') cartDrawer.close();

    this.previouslyFocused = document.activeElement;
    this.hidden = false;
    document.body.classList.add('fuda-upsell-open');

    const activeTab = this.querySelector('[data-fuda-tab].is-active') || this.tabs[0];
    if (activeTab) this.ensureCollectionLoaded(activeTab.dataset.fudaTab);

    requestAnimationFrame(() => this.panel?.focus());
  }

  close() {
    if (this.hidden) return;
    this.hidden = true;
    document.body.classList.remove('fuda-upsell-open');
    sessionStorage.setItem('fuda-upsell-dismissed', '1');
    if (this.previouslyFocused instanceof HTMLElement) this.previouslyFocused.focus();
  }
}

if (!customElements.get('fuda-second-product-popup')) {
  customElements.define('fuda-second-product-popup', FudaSecondProductPopup);
}
